import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, ArrowLeft, ExternalLink, Loader2 } from "lucide-react";
import api from "../lib/api";
import useSEO from "../hooks/useSEO";

export default function EventDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/events/${slug}`)
      .then((r) => setEvent(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useSEO({
    title: event ? event.title : "Événement",
    description: event ? event.description : "Détail d'un événement DemkaBiz",
  });

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 flex justify-center" data-testid="event-loading">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }
  if (error || !event) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center" data-testid="event-not-found">
        <h1 className="font-display font-bold text-3xl">Événement introuvable</h1>
        <p className="text-muted-foreground mt-3">Cet événement n'existe plus ou a été retiré.</p>
        <Link to="/agenda" className="btn-primary mt-6 inline-flex">
          <ArrowLeft className="w-4 h-4" /> Retour à l'agenda
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="event-detail-page">
      <Link to="/agenda" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-6" data-testid="event-back-link">
        <ArrowLeft className="w-4 h-4" /> Retour à l'agenda
      </Link>

      <div className="grid lg:grid-cols-[minmax(0,360px)_1fr] gap-10 items-start">
        {event.poster_url && (
          <div className="rounded-3xl overflow-hidden shadow-lg bg-secondary" data-testid="event-poster">
            <img
              src={event.poster_url}
              alt={`Affiche officielle — ${event.title}`}
              className="w-full h-auto object-contain"
            />
          </div>
        )}

        <div>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-accent/10 text-accent text-[11px] uppercase tracking-widest font-semibold" data-testid="event-tag">
            {event.tag}
          </span>
          {event.acronym && (
            <p className="text-xs uppercase tracking-widest text-primary font-bold mt-3">{event.acronym}</p>
          )}
          <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight leading-tight mt-2" data-testid="event-title">
            {event.title}
          </h1>

          <div className="flex flex-wrap items-center gap-5 text-sm text-muted-foreground mt-5" data-testid="event-meta">
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-primary" /> {event.date_label}</span>
            {event.city && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary" /> {event.city}</span>}
          </div>

          <p className="text-lg text-foreground/85 leading-relaxed mt-6" data-testid="event-description">
            {event.description}
          </p>

          {event.long_description && (
            <div className="mt-8 space-y-4 text-foreground/80 leading-relaxed whitespace-pre-line" data-testid="event-long-description">
              {event.long_description}
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-10">
            {event.external_url && (
              <a
                href={event.external_url}
                target="_blank"
                rel="noreferrer"
                className="btn-primary"
                data-testid="event-external-cta"
              >
                Site officiel <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <Link to="/contact" className="btn-outline" data-testid="event-contact-cta">
              Nous contacter
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
